/**
 * Render Part
 * Holds visual display information for an object
 */

import { Part } from '../Part';

/**
 * Render part - stores visual display data
 * Corresponds to Qud's Render part
 */
export class Render extends Part {
  readonly name = 'Render';

  /** Display name shown to player */
  displayName: string = '';

  /** ASCII render character */
  renderString: string = '';

  /** Color string in Qud format (e.g., "&Y^k") */
  colorString: string = '';

  /** Tile path for sprite rendering */
  tile: string = '';

  /** Tile color override */
  tileColor: string = '';

  /** Detail color (for dual-color sprites) */
  detailColor: string = '';

  /** Render layer for z-ordering */
  renderLayer: number = 0;

  /** Whether to render in dark areas */
  renderIfDark: boolean = true;

  /** Whether to flip horizontally */
  hFlip: boolean = false;

  constructor(params: Record<string, unknown> = {}) {
    super();
    // Map Qud parameter names to our properties
    if (params.DisplayName !== undefined) this.displayName = String(params.DisplayName);
    if (params.RenderString !== undefined) this.renderString = String(params.RenderString);
    if (params.ColorString !== undefined) this.colorString = String(params.ColorString);
    if (params.Tile !== undefined) this.tile = String(params.Tile);
    if (params.TileColor !== undefined) this.tileColor = String(params.TileColor);
    if (params.DetailColor !== undefined) this.detailColor = String(params.DetailColor);
    if (params.RenderLayer !== undefined) this.renderLayer = Number(params.RenderLayer);
    if (params.RenderIfDark !== undefined) this.renderIfDark = params.RenderIfDark !== 'false';
    if (params.HFlip !== undefined) this.hFlip = params.HFlip === 'true';
  }

  /**
   * Parse foreground and background colors from colorString
   * Format: "&<fg>^<bg>" or just "&<fg>"
   */
  getColors(): { foreground?: string; background?: string } {
    if (!this.colorString) return {};

    // Use tileColor if available, otherwise colorString
    const colorStr = this.tileColor || this.colorString;
    const parts = colorStr.split('^');
    const foreground = parts[0]?.replace('&', '').replace('amp;', '') || undefined;
    const background = parts[1] || undefined;

    return { foreground, background };
  }

  /**
   * Get all sprite colors including detail color
   * Used by the rendering system
   */
  getSpriteColors(): { foreground?: string; background?: string; detail?: string } {
    const colors = this.getColors();
    if (this.detailColor) {
      return { ...colors, detail: this.detailColor };
    }
    return colors;
  }

  /**
   * Get normalized sprite path for the atlas lookup
   * Converts "Terrain/sw_canyon.bmp" to "terrain_sw_canyon.bmp"
   */
  getNormalizedTile(): string {
    if (!this.tile) return '';
    return this.tile.replace(/\\/g, '/').replace(/\//g, '_').toLowerCase();
  }
}

/**
 * Factory for creating Render parts
 */
export function createRender(params: Record<string, unknown>): Render {
  return new Render(params);
}
